/**
 * Atelier Diamante — Ring Configurator v2 (Luxury Guided Flow)
 * File: assets/ad-ring-configurator.js
 *
 * Five-step guided journey pulling from live Shopify diamond inventory.
 * Steps: Shape → Preferences → Select Diamond → Design Setting → Review & Add to Cart
 *
 * The selected diamond product is added to cart; setting style + metal are
 * stored as line item properties for Dave's custom build workflow.
 *
 * Architecture: Alpine.js state machine, Shopify Collection AJAX API for
 * live inventory, /cart/add.js for add-to-cart.
 *
 * Required global (injected by Liquid section):
 *   window.AD_RC = {
 *     collectionHandle: 'lab-grown-diamonds',
 *     consultationUrl:  '/pages/contact',
 *     sectionId:        '<section.id>',
 *     cdnBase:          'https://pkonline.blob.core.windows.net/pkonline',
 *   }
 */

/* ==========================================================================
   SHAPE IMAGES — real diamond photos injected via window.AD_SHAPE_IMGS (Liquid)
   ========================================================================== */

/* AD_SHAPES and svgUri() removed — replaced with Shopify CDN photo URLs.
   window.AD_SHAPE_IMGS is set by the Liquid section before this script loads:
     { round: '...cdn.../ad-shape-round.jpg', oval: '...', ... }
*/

const AD_SHAPES_STUB = {

  // Round Brilliant — photorealistic colorless diamond, cool blue-gray palette
  round: `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="ro-b" cx="36%" cy="30%" r="70%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="28%" stop-color="#D8EAF4"/><stop offset="62%" stop-color="#5A8EAA"/><stop offset="100%" stop-color="#142432"/></radialGradient><radialGradient id="ro-t" cx="44%" cy="40%" r="54%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="50%" stop-color="#DCF0FF"/><stop offset="100%" stop-color="#88B8CC" stop-opacity="0.7"/></radialGradient><radialGradient id="ro-h" cx="28%" cy="22%" r="36%"><stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.9"/><stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient></defs><circle cx="60" cy="60" r="52" fill="url(#ro-b)"/><polygon points="60,8 82,26 60,38" fill="#0E1E2C" opacity="0.5"/><polygon points="112,60 94,38 82,60" fill="#0E1E2C" opacity="0.48"/><polygon points="60,112 38,94 60,82" fill="#0E1E2C" opacity="0.42"/><polygon points="8,60 26,82 38,60" fill="#0E1E2C" opacity="0.48"/><polygon points="60,8 38,26 60,38" fill="#CCE6F5" opacity="0.55"/><polygon points="8,60 26,38 38,60" fill="#CCE6F5" opacity="0.5"/><polygon points="60,112 82,94 60,82" fill="#BCDAEC" opacity="0.48"/><polygon points="112,60 94,82 82,60" fill="#BCDAEC" opacity="0.48"/><polygon points="60,38 76,44 82,60 76,76 60,82 44,76 38,60 44,44" fill="url(#ro-t)" stroke="#7AAEC4" stroke-width="0.8"/><line x1="60" y1="8" x2="60" y2="38" stroke="#4A80A0" stroke-width="0.7" opacity="0.75"/><line x1="112" y1="60" x2="82" y2="60" stroke="#4A80A0" stroke-width="0.7" opacity="0.75"/><line x1="60" y1="112" x2="60" y2="82" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="8" y1="60" x2="38" y2="60" stroke="#4A80A0" stroke-width="0.7" opacity="0.75"/><line x1="97" y1="23" x2="76" y2="44" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="97" y1="97" x2="76" y2="76" stroke="#4A80A0" stroke-width="0.55" opacity="0.55"/><line x1="23" y1="97" x2="44" y2="76" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="23" y1="23" x2="44" y2="44" stroke="#4A80A0" stroke-width="0.6" opacity="0.7"/><circle cx="60" cy="60" r="52" fill="url(#ro-h)"/><circle cx="60" cy="60" r="52" fill="none" stroke="#5A8EAA" stroke-width="1.5"/></svg>`,

  // Oval — elongated brilliant, cool colorless palette
  oval: `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="ov-b" cx="36%" cy="28%" r="70%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="28%" stop-color="#D8EAF4"/><stop offset="62%" stop-color="#5A8EAA"/><stop offset="100%" stop-color="#142432"/></radialGradient><radialGradient id="ov-t" cx="44%" cy="38%" r="54%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="50%" stop-color="#DCF0FF"/><stop offset="100%" stop-color="#88B8CC" stop-opacity="0.7"/></radialGradient><radialGradient id="ov-h" cx="28%" cy="20%" r="36%"><stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.9"/><stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient></defs><ellipse cx="60" cy="60" rx="36" ry="52" fill="url(#ov-b)"/><polygon points="60,8 74,28 60,34" fill="#0E1E2C" opacity="0.5"/><polygon points="96,60 78,44 76,60" fill="#0E1E2C" opacity="0.46"/><polygon points="60,112 46,92 60,86" fill="#0E1E2C" opacity="0.42"/><polygon points="24,60 42,76 44,60" fill="#0E1E2C" opacity="0.46"/><polygon points="60,8 46,28 60,34" fill="#CCE6F5" opacity="0.52"/><polygon points="24,60 42,44 44,60" fill="#CCE6F5" opacity="0.48"/><polygon points="60,112 74,92 60,86" fill="#BCDAEC" opacity="0.45"/><polygon points="96,60 78,76 76,60" fill="#BCDAEC" opacity="0.45"/><polygon points="60,34 74,42 76,60 74,78 60,86 46,78 44,60 46,42" fill="url(#ov-t)" stroke="#7AAEC4" stroke-width="0.8"/><line x1="60" y1="8" x2="60" y2="34" stroke="#4A80A0" stroke-width="0.7" opacity="0.75"/><line x1="96" y1="60" x2="76" y2="60" stroke="#4A80A0" stroke-width="0.7" opacity="0.75"/><line x1="60" y1="112" x2="60" y2="86" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="24" y1="60" x2="44" y2="60" stroke="#4A80A0" stroke-width="0.7" opacity="0.75"/><line x1="86" y1="22" x2="74" y2="42" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="86" y1="98" x2="74" y2="78" stroke="#4A80A0" stroke-width="0.55" opacity="0.55"/><line x1="34" y1="98" x2="46" y2="78" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="34" y1="22" x2="46" y2="42" stroke="#4A80A0" stroke-width="0.6" opacity="0.7"/><ellipse cx="60" cy="60" rx="36" ry="52" fill="url(#ov-h)"/><ellipse cx="60" cy="60" rx="36" ry="52" fill="none" stroke="#5A8EAA" stroke-width="1.5"/></svg>`,

  // Cushion — rounded square, cool colorless
  cushion: `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="cu-b" cx="36%" cy="30%" r="70%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="28%" stop-color="#D8EAF4"/><stop offset="62%" stop-color="#5A8EAA"/><stop offset="100%" stop-color="#142432"/></radialGradient><radialGradient id="cu-t" cx="44%" cy="40%" r="54%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="50%" stop-color="#DCF0FF"/><stop offset="100%" stop-color="#88B8CC" stop-opacity="0.7"/></radialGradient><radialGradient id="cu-h" cx="28%" cy="22%" r="36%"><stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.9"/><stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient></defs><rect x="14" y="14" width="92" height="92" rx="18" fill="url(#cu-b)"/><polygon points="60,14 106,14 80,40 60,40" fill="#0E1E2C" opacity="0.46"/><polygon points="106,60 106,106 80,80 80,60" fill="#0E1E2C" opacity="0.44"/><polygon points="60,106 14,106 40,80 60,80" fill="#0E1E2C" opacity="0.4"/><polygon points="14,60 14,14 40,40 40,60" fill="#0E1E2C" opacity="0.44"/><polygon points="14,14 60,14 60,40 40,40" fill="#CCE6F5" opacity="0.5"/><polygon points="60,106 106,106 106,60 80,60 80,80" fill="#BCDAEC" opacity="0.45"/><rect x="40" y="40" width="40" height="40" rx="6" fill="url(#cu-t)" stroke="#7AAEC4" stroke-width="0.8"/><line x1="60" y1="14" x2="60" y2="40" stroke="#4A80A0" stroke-width="0.7" opacity="0.7"/><line x1="106" y1="60" x2="80" y2="60" stroke="#4A80A0" stroke-width="0.7" opacity="0.7"/><line x1="60" y1="106" x2="60" y2="80" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><line x1="14" y1="60" x2="40" y2="60" stroke="#4A80A0" stroke-width="0.7" opacity="0.7"/><line x1="106" y1="14" x2="80" y2="40" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="106" y1="106" x2="80" y2="80" stroke="#4A80A0" stroke-width="0.55" opacity="0.55"/><line x1="14" y1="106" x2="40" y2="80" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="14" y1="14" x2="40" y2="40" stroke="#4A80A0" stroke-width="0.6" opacity="0.7"/><line x1="40" y1="40" x2="80" y2="80" stroke="#7AAEC4" stroke-width="0.4" opacity="0.3"/><line x1="80" y1="40" x2="40" y2="80" stroke="#7AAEC4" stroke-width="0.4" opacity="0.3"/><rect x="14" y="14" width="92" height="92" rx="18" fill="url(#cu-h)"/><rect x="14" y="14" width="92" height="92" rx="18" fill="none" stroke="#5A8EAA" stroke-width="1.5"/></svg>`,

  // Princess — square brilliant, strong X-cross, cool palette
  princess: `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="pr-b" cx="36%" cy="30%" r="70%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="28%" stop-color="#D8EAF4"/><stop offset="62%" stop-color="#5A8EAA"/><stop offset="100%" stop-color="#142432"/></radialGradient><radialGradient id="pr-t" cx="44%" cy="40%" r="54%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="50%" stop-color="#DCF0FF"/><stop offset="100%" stop-color="#88B8CC" stop-opacity="0.7"/></radialGradient><radialGradient id="pr-h" cx="28%" cy="22%" r="36%"><stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.9"/><stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient></defs><rect x="12" y="12" width="96" height="96" fill="url(#pr-b)"/><polygon points="12,12 60,12 60,60" fill="#0E1E2C" opacity="0.44"/><polygon points="108,12 108,60 60,60" fill="#CCE6F5" opacity="0.5"/><polygon points="108,108 60,108 60,60" fill="#0E1E2C" opacity="0.4"/><polygon points="12,108 12,60 60,60" fill="#CCE6F5" opacity="0.44"/><rect x="40" y="40" width="40" height="40" fill="url(#pr-t)" stroke="#7AAEC4" stroke-width="0.8"/><line x1="12" y1="12" x2="108" y2="108" stroke="#4A80A0" stroke-width="0.8" opacity="0.7"/><line x1="108" y1="12" x2="12" y2="108" stroke="#4A80A0" stroke-width="0.8" opacity="0.7"/><line x1="60" y1="12" x2="60" y2="108" stroke="#4A80A0" stroke-width="0.6" opacity="0.5"/><line x1="12" y1="60" x2="108" y2="60" stroke="#4A80A0" stroke-width="0.6" opacity="0.5"/><line x1="12" y1="12" x2="40" y2="40" stroke="#4A80A0" stroke-width="0.45" opacity="0.4"/><line x1="108" y1="12" x2="80" y2="40" stroke="#4A80A0" stroke-width="0.45" opacity="0.4"/><line x1="108" y1="108" x2="80" y2="80" stroke="#4A80A0" stroke-width="0.45" opacity="0.35"/><line x1="12" y1="108" x2="40" y2="80" stroke="#4A80A0" stroke-width="0.45" opacity="0.4"/><rect x="12" y="12" width="96" height="96" fill="url(#pr-h)"/><rect x="12" y="12" width="96" height="96" fill="none" stroke="#5A8EAA" stroke-width="1.5"/></svg>`,

  // Emerald — rectangular step-cut, hall-of-mirrors, cool palette
  emerald: `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="em-b" cx="36%" cy="30%" r="70%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="28%" stop-color="#D8EAF4"/><stop offset="62%" stop-color="#5A8EAA"/><stop offset="100%" stop-color="#142432"/></radialGradient><radialGradient id="em-t" cx="44%" cy="40%" r="54%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="50%" stop-color="#DCF0FF"/><stop offset="100%" stop-color="#88B8CC" stop-opacity="0.7"/></radialGradient><radialGradient id="em-h" cx="28%" cy="22%" r="36%"><stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.9"/><stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient></defs><polygon points="28,10 92,10 110,28 110,92 92,110 28,110 10,92 10,28" fill="url(#em-b)"/><polygon points="10,28 28,10 28,44 10,44" fill="#0E1E2C" opacity="0.44"/><polygon points="92,10 110,28 110,44 92,44" fill="#CCE6F5" opacity="0.5"/><polygon points="110,92 92,110 92,76 110,76" fill="#0E1E2C" opacity="0.4"/><polygon points="28,110 10,92 10,76 28,76" fill="#CCE6F5" opacity="0.44"/><polygon points="44,28 76,28 92,44 92,76 76,92 44,92 28,76 28,44" fill="none" stroke="#7AAEC4" stroke-width="0.9" opacity="0.7"/><rect x="44" y="44" width="32" height="32" fill="url(#em-t)" stroke="#7AAEC4" stroke-width="0.8"/><line x1="44" y1="51" x2="76" y2="51" stroke="#4A80A0" stroke-width="0.5" opacity="0.5"/><line x1="44" y1="60" x2="76" y2="60" stroke="#4A80A0" stroke-width="0.5" opacity="0.5"/><line x1="44" y1="69" x2="76" y2="69" stroke="#4A80A0" stroke-width="0.5" opacity="0.5"/><line x1="51" y1="44" x2="51" y2="76" stroke="#4A80A0" stroke-width="0.35" opacity="0.3"/><line x1="69" y1="44" x2="69" y2="76" stroke="#4A80A0" stroke-width="0.35" opacity="0.3"/><line x1="10" y1="28" x2="28" y2="44" stroke="#4A80A0" stroke-width="0.7" opacity="0.7"/><line x1="110" y1="28" x2="92" y2="44" stroke="#4A80A0" stroke-width="0.7" opacity="0.7"/><line x1="110" y1="92" x2="92" y2="76" stroke="#4A80A0" stroke-width="0.65" opacity="0.6"/><line x1="10" y1="92" x2="28" y2="76" stroke="#4A80A0" stroke-width="0.65" opacity="0.65"/><line x1="28" y1="10" x2="28" y2="44" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><line x1="92" y1="10" x2="92" y2="44" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><line x1="110" y1="60" x2="76" y2="60" stroke="#4A80A0" stroke-width="0.5" opacity="0.4"/><line x1="10" y1="60" x2="44" y2="60" stroke="#4A80A0" stroke-width="0.5" opacity="0.4"/><polygon points="28,10 92,10 110,28 110,92 92,110 28,110 10,92 10,28" fill="url(#em-h)"/><polygon points="28,10 92,10 110,28 110,92 92,110 28,110 10,92 10,28" fill="none" stroke="#5A8EAA" stroke-width="1.5"/></svg>`,

  // Pear — teardrop brilliant, cool colorless
  pear: `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="pe-b" cx="38%" cy="28%" r="70%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="28%" stop-color="#D8EAF4"/><stop offset="62%" stop-color="#5A8EAA"/><stop offset="100%" stop-color="#142432"/></radialGradient><radialGradient id="pe-t" cx="44%" cy="36%" r="54%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="50%" stop-color="#DCF0FF"/><stop offset="100%" stop-color="#88B8CC" stop-opacity="0.7"/></radialGradient><radialGradient id="pe-h" cx="30%" cy="18%" r="36%"><stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.9"/><stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient></defs><path d="M60,10 C80,10 96,26 96,46 C96,68 80,92 60,112 C40,92 24,68 24,46 C24,26 40,10 60,10Z" fill="url(#pe-b)"/><polygon points="60,10 82,24 60,40" fill="#0E1E2C" opacity="0.48"/><polygon points="96,52 80,56 76,60" fill="#0E1E2C" opacity="0.44"/><polygon points="60,112 44,92 60,82" fill="#0E1E2C" opacity="0.4"/><polygon points="24,52 40,56 44,60" fill="#0E1E2C" opacity="0.44"/><polygon points="60,10 38,24 60,40" fill="#CCE6F5" opacity="0.52"/><polygon points="24,52 40,44 44,60" fill="#CCE6F5" opacity="0.46"/><path d="M60,40 C70,40 78,48 78,56 C78,70 70,84 60,90 C50,84 42,70 42,56 C42,48 50,40 60,40Z" fill="url(#pe-t)" stroke="#7AAEC4" stroke-width="0.8"/><line x1="60" y1="10" x2="60" y2="40" stroke="#4A80A0" stroke-width="0.7" opacity="0.75"/><line x1="90" y1="24" x2="76" y2="46" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="96" y1="52" x2="78" y2="54" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><line x1="88" y1="76" x2="72" y2="66" stroke="#4A80A0" stroke-width="0.6" opacity="0.55"/><line x1="74" y1="96" x2="64" y2="78" stroke="#4A80A0" stroke-width="0.55" opacity="0.5"/><line x1="60" y1="112" x2="60" y2="90" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><line x1="46" y1="96" x2="56" y2="78" stroke="#4A80A0" stroke-width="0.55" opacity="0.5"/><line x1="32" y1="76" x2="48" y2="66" stroke="#4A80A0" stroke-width="0.6" opacity="0.55"/><line x1="24" y1="52" x2="42" y2="54" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><line x1="30" y1="24" x2="44" y2="46" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><path d="M60,10 C80,10 96,26 96,46 C96,68 80,92 60,112 C40,92 24,68 24,46 C24,26 40,10 60,10Z" fill="url(#pe-h)"/><path d="M60,10 C80,10 96,26 96,46 C96,68 80,92 60,112 C40,92 24,68 24,46 C24,26 40,10 60,10Z" fill="none" stroke="#5A8EAA" stroke-width="1.5"/></svg>`,

  // Radiant — trimmed-corner rectangle, brilliant facets, cool palette
  radiant: `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="ra-b" cx="36%" cy="30%" r="70%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="28%" stop-color="#D8EAF4"/><stop offset="62%" stop-color="#5A8EAA"/><stop offset="100%" stop-color="#142432"/></radialGradient><radialGradient id="ra-t" cx="44%" cy="40%" r="54%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="50%" stop-color="#DCF0FF"/><stop offset="100%" stop-color="#88B8CC" stop-opacity="0.7"/></radialGradient><radialGradient id="ra-h" cx="28%" cy="22%" r="36%"><stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.9"/><stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient></defs><polygon points="24,10 96,10 110,24 110,96 96,110 24,110 10,96 10,24" fill="url(#ra-b)"/><polygon points="24,10 60,10 60,42 36,42" fill="#0E1E2C" opacity="0.44"/><polygon points="96,10 110,24 110,60 84,42" fill="#CCE6F5" opacity="0.5"/><polygon points="96,110 60,110 60,78 84,78" fill="#0E1E2C" opacity="0.4"/><polygon points="24,110 10,96 10,60 36,78" fill="#CCE6F5" opacity="0.44"/><rect x="36" y="42" width="48" height="36" fill="url(#ra-t)" stroke="#7AAEC4" stroke-width="0.8"/><line x1="36" y1="42" x2="84" y2="78" stroke="#7AAEC4" stroke-width="0.5" opacity="0.4"/><line x1="84" y1="42" x2="36" y2="78" stroke="#7AAEC4" stroke-width="0.5" opacity="0.4"/><line x1="60" y1="10" x2="60" y2="42" stroke="#4A80A0" stroke-width="0.7" opacity="0.7"/><line x1="110" y1="60" x2="84" y2="60" stroke="#4A80A0" stroke-width="0.7" opacity="0.7"/><line x1="60" y1="110" x2="60" y2="78" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><line x1="10" y1="60" x2="36" y2="60" stroke="#4A80A0" stroke-width="0.7" opacity="0.7"/><line x1="10" y1="24" x2="36" y2="42" stroke="#4A80A0" stroke-width="0.65" opacity="0.65"/><line x1="24" y1="10" x2="36" y2="42" stroke="#4A80A0" stroke-width="0.65" opacity="0.65"/><line x1="96" y1="10" x2="84" y2="42" stroke="#4A80A0" stroke-width="0.65" opacity="0.65"/><line x1="110" y1="24" x2="84" y2="42" stroke="#4A80A0" stroke-width="0.65" opacity="0.65"/><line x1="110" y1="96" x2="84" y2="78" stroke="#4A80A0" stroke-width="0.6" opacity="0.55"/><line x1="96" y1="110" x2="84" y2="78" stroke="#4A80A0" stroke-width="0.6" opacity="0.55"/><line x1="24" y1="110" x2="36" y2="78" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><line x1="10" y1="96" x2="36" y2="78" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><polygon points="24,10 96,10 110,24 110,96 96,110 24,110 10,96 10,24" fill="url(#ra-h)"/><polygon points="24,10 96,10 110,24 110,96 96,110 24,110 10,96 10,24" fill="none" stroke="#5A8EAA" stroke-width="1.5"/></svg>`,

  // Marquise — elongated eye shape, cool colorless
  marquise: `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="ma-b" cx="36%" cy="30%" r="70%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="28%" stop-color="#D8EAF4"/><stop offset="62%" stop-color="#5A8EAA"/><stop offset="100%" stop-color="#142432"/></radialGradient><radialGradient id="ma-t" cx="44%" cy="40%" r="54%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="50%" stop-color="#DCF0FF"/><stop offset="100%" stop-color="#88B8CC" stop-opacity="0.7"/></radialGradient><radialGradient id="ma-h" cx="28%" cy="22%" r="36%"><stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.9"/><stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient></defs><path d="M60,8 C78,18 100,34 100,60 C100,86 78,102 60,112 C42,102 20,86 20,60 C20,34 42,18 60,8Z" fill="url(#ma-b)"/><polygon points="60,8 84,28 76,60" fill="#0E1E2C" opacity="0.46"/><polygon points="100,46 100,74 76,60" fill="#0E1E2C" opacity="0.42"/><polygon points="60,112 36,92 44,60" fill="#0E1E2C" opacity="0.38"/><polygon points="60,8 36,28 44,60" fill="#CCE6F5" opacity="0.5"/><polygon points="20,46 20,74 44,60" fill="#CCE6F5" opacity="0.46"/><polygon points="60,112 84,92 76,60" fill="#BCDAEC" opacity="0.42"/><path d="M60,38 C70,38 76,46 76,60 C76,74 70,82 60,82 C50,82 44,74 44,60 C44,46 50,38 60,38Z" fill="url(#ma-t)" stroke="#7AAEC4" stroke-width="0.8"/><line x1="60" y1="8" x2="60" y2="38" stroke="#4A80A0" stroke-width="0.7" opacity="0.75"/><line x1="100" y1="60" x2="76" y2="60" stroke="#4A80A0" stroke-width="0.7" opacity="0.7"/><line x1="60" y1="112" x2="60" y2="82" stroke="#4A80A0" stroke-width="0.65" opacity="0.65"/><line x1="20" y1="60" x2="44" y2="60" stroke="#4A80A0" stroke-width="0.7" opacity="0.7"/><line x1="88" y1="22" x2="74" y2="46" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="100" y1="42" x2="78" y2="54" stroke="#4A80A0" stroke-width="0.55" opacity="0.55"/><line x1="100" y1="78" x2="78" y2="66" stroke="#4A80A0" stroke-width="0.55" opacity="0.5"/><line x1="88" y1="98" x2="74" y2="74" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><line x1="32" y1="22" x2="46" y2="46" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="20" y1="42" x2="42" y2="54" stroke="#4A80A0" stroke-width="0.55" opacity="0.55"/><line x1="20" y1="78" x2="42" y2="66" stroke="#4A80A0" stroke-width="0.55" opacity="0.5"/><line x1="32" y1="98" x2="46" y2="74" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><path d="M60,8 C78,18 100,34 100,60 C100,86 78,102 60,112 C42,102 20,86 20,60 C20,34 42,18 60,8Z" fill="url(#ma-h)"/><path d="M60,8 C78,18 100,34 100,60 C100,86 78,102 60,112 C42,102 20,86 20,60 C20,34 42,18 60,8Z" fill="none" stroke="#5A8EAA" stroke-width="1.5"/></svg>`,

  // Heart — brilliant-cut heart, cool colorless
  heart: `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="he-b" cx="38%" cy="35%" r="68%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="28%" stop-color="#D8EAF4"/><stop offset="62%" stop-color="#5A8EAA"/><stop offset="100%" stop-color="#142432"/></radialGradient><radialGradient id="he-t" cx="44%" cy="44%" r="54%"><stop offset="0%" stop-color="#FFFFFF"/><stop offset="50%" stop-color="#DCF0FF"/><stop offset="100%" stop-color="#88B8CC" stop-opacity="0.7"/></radialGradient><radialGradient id="he-h" cx="30%" cy="24%" r="36%"><stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.9"/><stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/></radialGradient></defs><path d="M60,106 C42,88 12,68 12,42 C12,24 26,14 40,14 C50,14 57,20 60,28 C63,20 70,14 80,14 C94,14 108,24 108,42 C108,68 78,88 60,106Z" fill="url(#he-b)"/><polygon points="60,28 80,14 70,44 60,54" fill="#0E1E2C" opacity="0.44"/><polygon points="108,42 108,68 76,64 70,44" fill="#0E1E2C" opacity="0.42"/><polygon points="60,106 42,88 50,70 60,68" fill="#0E1E2C" opacity="0.38"/><polygon points="60,28 40,14 50,44 60,54" fill="#CCE6F5" opacity="0.5"/><polygon points="12,42 12,68 44,64 50,44" fill="#CCE6F5" opacity="0.46"/><polygon points="60,106 78,88 70,70 60,68" fill="#BCDAEC" opacity="0.4"/><path d="M60,54 C52,54 46,58 44,64 C44,72 52,78 60,84 C68,78 76,72 76,64 C74,58 68,54 60,54Z" fill="url(#he-t)" stroke="#7AAEC4" stroke-width="0.8"/><line x1="60" y1="28" x2="60" y2="54" stroke="#4A80A0" stroke-width="0.7" opacity="0.75"/><line x1="60" y1="106" x2="60" y2="68" stroke="#4A80A0" stroke-width="0.65" opacity="0.65"/><line x1="40" y1="14" x2="50" y2="44" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="80" y1="14" x2="70" y2="44" stroke="#4A80A0" stroke-width="0.6" opacity="0.65"/><line x1="12" y1="42" x2="36" y2="50" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><line x1="108" y1="42" x2="84" y2="50" stroke="#4A80A0" stroke-width="0.6" opacity="0.6"/><line x1="22" y1="68" x2="44" y2="64" stroke="#4A80A0" stroke-width="0.55" opacity="0.5"/><line x1="98" y1="68" x2="76" y2="64" stroke="#4A80A0" stroke-width="0.55" opacity="0.5"/><line x1="38" y1="90" x2="54" y2="76" stroke="#4A80A0" stroke-width="0.5" opacity="0.45"/><line x1="82" y1="90" x2="66" y2="76" stroke="#4A80A0" stroke-width="0.5" opacity="0.45"/><path d="M60,106 C42,88 12,68 12,42 C12,24 26,14 40,14 C50,14 57,20 60,28 C63,20 70,14 80,14 C94,14 108,24 108,42 C108,68 78,88 60,106Z" fill="url(#he-h)"/><path d="M60,106 C42,88 12,68 12,42 C12,24 26,14 40,14 C50,14 57,20 60,28 C63,20 70,14 80,14 C94,14 108,24 108,42 C108,68 78,88 60,106Z" fill="none" stroke="#5A8EAA" stroke-width="1.5"/></svg>`,

};

function svgUri(svg) {
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg.trim());
}

/**
 * Returns the best image src for a given shape key.
 * Prefers the real photo from window.AD_SHAPE_IMGS (uploaded to Shopify assets).
 * Falls back to the SVG stub if the photo isn't available yet.
 */
function shapeImgSrc(key) {
  const imgs = window.AD_SHAPE_IMGS || {};
  if (imgs[key]) return imgs[key];
  const stub = AD_SHAPES_STUB[key] || AD_SHAPES_STUB.round;
  return svgUri(stub);
}

/* ==========================================================================
   FANCY COLOR PREVIEWS — generated tiles injected via window.AD_FANCY_IMGS (Liquid)
   ========================================================================== */
const FANCY_INFO = {
  yellow: { label: 'Yellow', tagline: 'Warm and sunlit. Our most-loved fancy color.' },
  pink:   { label: 'Pink',   tagline: 'Romantic and rare. A true statement stone.' },
  blue:   { label: 'Blue',   tagline: 'Cool, deep, and unmistakable.' },
  red:    { label: 'Red',    tagline: 'The rarest fancy color of all.' },
  green:  { label: 'Green',  tagline: 'Exceptionally rare and quietly striking.' },
};
function fancyImgSrc(hue) {
  const imgs = window.AD_FANCY_IMGS || {};
  return imgs[hue] || null;
}

/* ==========================================================================
   SHAPE DESCRIPTIONS — educational context for luxury buyers
   ========================================================================== */

const SHAPE_INFO = {
  round:    { label: 'Round',           tagline: 'The classic. Maximum fire and brilliance.' },
  oval:     { label: 'Oval',            tagline: 'Elongated elegance. Appears larger on the finger.' },
  cushion:  { label: 'Cushion',         tagline: 'Romantic pillow shape with soft, rounded corners.' },
  princess: { label: 'Princess',        tagline: 'Modern square cut with exceptional sparkle.' },
  emerald:  { label: 'Emerald',         tagline: 'Art Deco sophistication. Hall-of-mirrors brilliance.' },
  pear:     { label: 'Pear',            tagline: 'Teardrop grace. Flatters every hand.' },
  radiant:  { label: 'Radiant',         tagline: 'Trimmed corners with brilliant-cut facets.' },
  marquise: { label: 'Marquise',        tagline: 'Dramatic and regal. Maximizes perceived carat weight.' },
  heart:    { label: 'Heart',           tagline: 'The ultimate symbol of love. Bold and unmistakable.' },
  rare:     { label: 'Rare Cuts',       tagline: 'Kite, trillion, pentagon and more. One of one, never repeated.' },
};

/* ==========================================================================
   SETTING STYLES
   ========================================================================== */

/* ── SETTING PRICES ── adder on top of diamond price. Update these to match your margins. */
const SETTINGS = [
  {
    key: 'solitaire',
    label: 'Solitaire',
    desc: 'One stone. All the attention. The most timeless choice.',
    price: 800,   /* USD adder */
    icon: `<svg viewBox="0 0 60 60" fill="none" stroke="#C9A96E" stroke-width="1.2"><line x1="30" y1="50" x2="30" y2="28"/><polygon points="30,10 42,24 18,24" fill="#F7F3ED" stroke="#C9A96E"/><line x1="10" y1="50" x2="50" y2="50" stroke-width="2"/></svg>`,
  },
  {
    key: 'halo',
    label: 'Halo',
    desc: 'A circle of smaller diamonds amplifies the center stone.',
    price: 1500,
    icon: `<svg viewBox="0 0 60 60" fill="none" stroke="#C9A96E" stroke-width="1.2"><circle cx="30" cy="22" r="10" fill="#F7F3ED"/><circle cx="30" cy="22" r="15" stroke-dasharray="3 3"/><line x1="30" y1="37" x2="30" y2="48"/><line x1="10" y1="50" x2="50" y2="50" stroke-width="2"/></svg>`,
  },
  {
    key: 'three-stone',
    label: 'Three Stone',
    desc: 'Past, present, future. Two side stones frame the center.',
    price: 2200,
    icon: `<svg viewBox="0 0 60 60" fill="none" stroke="#C9A96E" stroke-width="1.2"><polygon points="30,8 40,22 20,22" fill="#F7F3ED"/><polygon points="14,14 20,24 8,24" fill="#F7F3ED"/><polygon points="46,14 52,24 40,24" fill="#F7F3ED"/><line x1="10" y1="50" x2="50" y2="50" stroke-width="2"/><line x1="30" y1="22" x2="30" y2="48"/></svg>`,
  },
  {
    key: 'pave',
    label: 'Pavé',
    desc: 'Tiny diamonds set into the band for continuous sparkle.',
    price: 2000,
    icon: `<svg viewBox="0 0 60 60" fill="none" stroke="#C9A96E" stroke-width="1.2"><polygon points="30,10 42,24 18,24" fill="#F7F3ED"/><line x1="30" y1="24" x2="30" y2="48"/><circle cx="22" cy="50" r="2" fill="#C9A96E"/><circle cx="30" cy="50" r="2" fill="#C9A96E"/><circle cx="38" cy="50" r="2" fill="#C9A96E"/><circle cx="14" cy="50" r="2" fill="#C9A96E"/><circle cx="46" cy="50" r="2" fill="#C9A96E"/></svg>`,
  },
  {
    key: 'cathedral',
    label: 'Cathedral Pavé',
    desc: 'Arched shoulders set with micro-pavé diamonds lift the center stone with architectural grace.',
    price: 1800,
    icon: `<svg viewBox="0 0 60 60" fill="none" stroke="#C9A96E" stroke-width="1.2"><polygon points="30,8 40,22 20,22" fill="#F7F3ED"/><path d="M20,24 Q14,36 10,50" stroke-width="2"/><path d="M40,24 Q46,36 50,50" stroke-width="2"/><circle cx="14" cy="46" r="1.5" fill="#C9A96E"/><circle cx="17" cy="40" r="1.5" fill="#C9A96E"/><circle cx="46" cy="46" r="1.5" fill="#C9A96E"/><circle cx="43" cy="40" r="1.5" fill="#C9A96E"/></svg>`,
  },
  {
    key: 'vintage',
    label: 'Vintage Milgrain',
    desc: 'Milgrain bead edges, filigree details, and romantic old-world craftsmanship.',
    price: 1800,
    icon: `<svg viewBox="0 0 60 60" fill="none" stroke="#C9A96E" stroke-width="1.2"><polygon points="30,10 42,24 18,24" fill="#F7F3ED"/><line x1="30" y1="24" x2="30" y2="48"/><line x1="10" y1="50" x2="50" y2="50" stroke-width="2" stroke-dasharray="2 2"/><circle cx="30" cy="22" r="15" stroke-dasharray="1.5 2.5" opacity="0.5"/></svg>`,
  },
];

/* ==========================================================================
   METALS
   ========================================================================== */

/* ── METAL PRICES ── 14K is included in the setting price. 18K and Platinum add $350. */
const METALS = [
  { key: 'platinum',    label: 'Platinum',          swatch: '#D4D4D8', price: 350,  included: false, desc: 'Hypoallergenic. The most durable precious metal.' },
  { key: '14k-white',   label: '14K White Gold',    swatch: '#E8E4DF', price: 0,    included: true,  desc: 'Bright white finish. Rhodium plated for extra shine.' },
  { key: '14k-yellow',  label: '14K Yellow Gold',   swatch: '#C9A96E', price: 0,    included: true,  desc: 'Warm and classic. The traditional choice.' },
  { key: '14k-rose',    label: '14K Rose Gold',     swatch: '#B76E79', price: 0,    included: true,  desc: 'Blush pink warmth. Romantic and modern.' },
  { key: '18k-white',   label: '18K White Gold',    swatch: '#F0EDE8', price: 350,  included: false, desc: 'Higher gold content. Brilliant white luster.' },
  { key: '18k-yellow',  label: '18K Yellow Gold',   swatch: '#D4A843', price: 350,  included: false, desc: 'Richer, deeper gold tone. The luxurious standard.' },
  { key: '18k-rose',    label: '18K Rose Gold',     swatch: '#C4717C', price: 350,  included: false, desc: 'Deeper blush tone. Timeless romantic warmth.' },
];

/* ==========================================================================
   CRAFTSMANSHIP / INTRICACY LEVELS — price adder on top of setting
   ========================================================================== */

const INTRICACY_OPTIONS = [
  {
    key:   'standard',
    label: 'Standard',
    badge: 'Included',
    price: 0,
    desc:  'Expertly crafted to the selected setting style.',
  },
  {
    key:   'enhanced',
    label: 'Enhanced',
    badge: '+ $500',
    price: 500,
    desc:  'Elevated detail work — extra hand-finishing and refinement.',
  },
  {
    key:   'bespoke',
    label: 'Bespoke',
    badge: '+ $1,500',
    price: 1500,
    desc:  'Maximum intricacy — fully custom handcraft, unique to your piece.',
  },
];

/* ==========================================================================
   MONEY FORMATTER — Shopify prices are dollar strings, e.g. "8465.68"
   ========================================================================== */

function _money(amount) {
  const val = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

/* ==========================================================================
   SHOPIFY COLLECTION AJAX — fetches live diamonds
   ========================================================================== */

let _adCatalog = null; /* full catalog cache — fetched once per page load */

async function fetchDiamonds(collectionHandle, filters = {}) {
  const { shape, caratMin, caratMax, budgetMin, budgetMax, colorType, qualityTier, fancyHue } = filters;

  /* Shopify /collections/{handle}/products.json — no server-side tag filtering
     available on storefront API, so we paginate and filter client-side.
     Parallel fetching (batches of 8 pages) avoids sequential wait.
     The loose-diamonds catalog has white diamonds starting around page 10,
     so we need to fetch enough pages to cover the full inventory. */
  const baseUrl = `/collections/${collectionHandle}/products.json?limit=250`;
  const TOTAL_PAGES = 28; /* covers ~7000 products */
  const BATCH_SIZE  = 8;  /* parallel requests per batch */

  if (_adCatalog) return adFilterDiamonds(_adCatalog, filters);

  const allProducts = [];

  for (let batchStart = 1; batchStart <= TOTAL_PAGES; batchStart += BATCH_SIZE) {
    const pages = [];
    for (let p = batchStart; p < batchStart + BATCH_SIZE && p <= TOTAL_PAGES; p++) {
      pages.push(p);
    }
    const results = await Promise.all(
      pages.map(p =>
        fetch(baseUrl + `&page=${p}`)
          .then(r => r.ok ? r.json() : { products: [] })
          .then(d => d.products || [])
          .catch(() => [])
      )
    );
    let batchEmpty = true;
    for (const batch of results) {
      if (batch.length > 0) { allProducts.push(...batch); batchEmpty = false; }
    }
    /* Stop if all pages in the batch came back empty */
    if (batchEmpty) break;
    /* Also stop if the last page in the batch was short (end of collection) */
    const lastBatch = results[results.length - 1];
    if (lastBatch.length > 0 && lastBatch.length < 250) break;
  }

  _adCatalog = allProducts;
  return adFilterDiamonds(allProducts, filters);
}

/* Client-side filter: shape, carat, price, color type, quality tier */
function adFilterDiamonds(products, filters) {
  const { shape, caratMin, caratMax, budgetMin, budgetMax, colorType, qualityTier, fancyHue } = filters;
  return products.filter(p => {
    const variant = p.variants?.[0];
    if (!variant) return false;
    const title  = (p.title || '').toLowerCase();
    const price  = parseFloat(variant.price) || 0;
    const carat  = parseFloat(_getMetafield(p, 'carat_weight') || _getMetafield(p, 'diamond_carat')) || 0;
    const pColorType = _tagValue(p, 'color-type') || 'colorless';

    /* Diamond type: 'colorless' → white diamonds; 'fancy' → color diamonds */
    if (colorType && colorType !== 'all') {
      if (colorType === 'colorless' && pColorType !== 'colorless') return false;
      if (colorType === 'fancy' && pColorType === 'colorless') return false;
    }
    /* Fancy hue narrowing (only when a specific color is chosen) */
    if (colorType === 'fancy' && fancyHue) {
      if ((_tagValue(p, 'color-hue') || '').toLowerCase() !== fancyHue) return false;
    }

    /* Shape: rare cuts are their own exclusive set; standard shapes exclude them */
    const isRareCut = (_tagValue(p, 'cut-style') || '').toLowerCase() === 'rare';
    if (shape) {
      if (shape === 'rare') {
        if (!isRareCut) return false;
      } else {
        if (isRareCut) return false;
        const shapeTag = _tagValue(p, 'shape');
        const matchTag   = shapeTag && shapeTag.toLowerCase() === shape.toLowerCase();
        const matchTitle = title.includes(shape.toLowerCase());
        if (!matchTag && !matchTitle) return false;
      }
    }

    if (caratMin && carat < caratMin) return false;
    if (caratMax && carat > caratMax) return false;
    if (budgetMin && price < budgetMin) return false;
    if (budgetMax && price > budgetMax) return false;

    /* Quality tier: plain-English presets mapped to color/clarity tags.
       heirloom  = D-F color (colorless only) + VVS2 or better
       sweetspot = D-H color (colorless only) + VS2 or better
       presence  = no quality floor; results sorted by carat afterwards */
    if (qualityTier && qualityTier !== 'presence') {
      const CL_ORDER = ['fl', 'if', 'vvs1', 'vvs2', 'vs1', 'vs2', 'si1', 'si2'];
      const clar  = (_tagValue(p, 'clarity') || '').toLowerCase();
      const clIdx = CL_ORDER.indexOf(clar);
      const colorVal = (_tagValue(p, 'color') || '').toLowerCase();
      if (qualityTier === 'heirloom') {
        if (clIdx === -1 || clIdx > CL_ORDER.indexOf('vvs2')) return false;
        if (pColorType === 'colorless' && !['d', 'e', 'f'].includes(colorVal)) return false;
      } else if (qualityTier === 'sweetspot') {
        if (clIdx === -1 || clIdx > CL_ORDER.indexOf('vs2')) return false;
        if (pColorType === 'colorless' && !['d', 'e', 'f', 'g', 'h'].includes(colorVal)) return false;
      }
    }

    return true;
  });
}

/* Retrieve a tag value by its key prefix from a product.
   Tags on this catalog follow the format "key:value", e.g. "color:d", "clarity:vvs2" */
function _tagValue(product, tagKey) {
  const prefix = tagKey.toLowerCase() + ':';
  for (const tag of (product.tags || [])) {
    if (tag.toLowerCase().startsWith(prefix)) {
      return tag.substring(prefix.length).trim();
    }
  }
  return null;
}

/* Extract metafield-like values from product JSON (collection endpoint format).
   Priority: structured tags → title regex fallback */
function _getMetafield(product, key) {
  /* Map logical keys to their actual tag prefixes in this catalog */
  const TAG_ALIASES = {
    carat_weight:    'carat',
    diamond_carat:   'carat',
    diamond_color:   'color',
    color_grade:     'color',
    diamond_clarity: 'clarity',
    clarity_grade:   'clarity',
    diamond_shape:   'shape',
    shape:           'shape',
    diamond_cut:     'cut',
  };

  /* Strategy 1: structured tag lookup */
  const tagKey = TAG_ALIASES[key] || key.replace(/_/g, '-');
  const tagVal = _tagValue(product, tagKey);
  if (tagVal !== null) return tagVal;

  /* Strategy 2: title regex fallback */
  const title = product.title || '';
  if (key === 'carat_weight' || key === 'diamond_carat') {
    const m = title.match(/([\d.]+)\s*ct/i);
    return m ? m[1] : null;
  }
  if (key === 'diamond_color' || key === 'color_grade') {
    /* "D Color," format (colorless) or "Fancy Vivid Pink" from title — tag is preferred */
    const m = title.match(/\b([D-N])\s+Color/i);
    return m ? m[1] : null;
  }
  if (key === 'diamond_clarity' || key === 'clarity_grade') {
    /* ",\s*VVS2," format */
    const m = title.match(/,\s*([A-Z]{2,4}\d?)\s*,/i);
    return m ? m[1] : null;
  }
  if (key === 'diamond_shape' || key === 'shape') {
    const shapes = ['round', 'oval', 'cushion', 'princess', 'emerald', 'pear', 'radiant', 'marquise', 'heart', 'crisscut'];
    const lower  = title.toLowerCase();
    return shapes.find(s => lower.includes(s)) || null;
  }

  return null;
}

/* Parse diamond details from a Shopify product object */
function parseDiamond(product) {
  const variant   = product.variants?.[0];
  const title     = product.title || '';
  const price     = parseFloat(variant?.price) || 0;
  /* Prefer exact carat from title ("2.51ct") over range-based tag ("2-3ct" → parseFloat = 2) */
  const caratFromTitle = parseFloat((title.match(/([\d.]+)\s*ct/i) || [])[1]) || 0;
  const caratFromTag   = parseFloat(_getMetafield(product, 'carat_weight') || _getMetafield(product, 'diamond_carat')) || 0;
  const carat          = caratFromTitle > 0 ? caratFromTitle : caratFromTag;
  const colorType = _tagValue(product, 'color-type') || 'colorless';

  /* Color display: "D" for colorless, "Fancy Vivid Pink" for fancy */
  let color = _getMetafield(product, 'diamond_color') || '—';
  if (color && color !== '—') {
    /* Colorless grades are stored as single letter — uppercase */
    if (colorType === 'colorless' && color.length <= 2) {
      color = color.toUpperCase();
    } else {
      /* Fancy colors — title-case */
      color = color.charAt(0).toUpperCase() + color.slice(1);
    }
  }

  let clarity = _getMetafield(product, 'diamond_clarity') || '—';
  if (clarity && clarity !== '—') clarity = clarity.toUpperCase();

  let shape = _getMetafield(product, 'diamond_shape') || '—';
  if (shape && shape !== '—') shape = shape.charAt(0).toUpperCase() + shape.slice(1);

  const images  = (product.images || []).map(i => i.src).filter(Boolean);
  const image   = images[0] || '';
  const handle  = product.handle || '';
  const stoneId = handle.replace('lab-diamond-', '').toUpperCase();

  return {
    id: product.id,
    variantId: variant?.id,
    handle,
    title,
    stoneId,
    colorType,
    certificate: (_tagValue(product, 'lab') || '').toUpperCase(),
    price,
    carat,
    color,
    clarity,
    shape,
    image,
    images,  /* all spin frames */
    available: variant?.available !== false,
  };
}

/* ==========================================================================
   ALPINE.JS COMPONENT
   ========================================================================== */

/* ── AI Custom Design pricing config (tunable) ───────────────────────────── */
const PRICING = {
  base: 1200,
  structural: {
    'halo': 1500, 'hidden halo': 1700, 'double halo': 2200,
    'pave': 2000, 'eternity': 2000, 'half pave': 1000,
    'three stone': 2200, 'five stone': 2800, 'side stones': 900,
    'cathedral': 1800, 'bezel': 700, 'tension': 1100,
    'toi et moi': 1400, 'bypass': 1400
  },
  detailing: {
    'milgrain': 500, 'filigree': 800, 'engrav': 250,
    'vintage': 900, 'art deco': 900, 'floral': 700, 'nature': 700,
    'two tone': 500, 'mixed metal': 500, 'fancy color': 1200,
    'sapphire': 1200, 'emerald': 1200, 'ruby': 1200,
    'gallery': 400, 'hand engrav': 450
  },
  synonyms: {
    'micropave': 'pave', 'micro pave': 'pave', 'pav\u00e9': 'pave', 'full pave': 'pave',
    'surprise halo': 'hidden halo', 'secret halo': 'hidden halo', 'peekaboo halo': 'hidden halo',
    'trilogy': 'three stone', 'past present future': 'three stone', 'three-stone': 'three stone',
    'five-stone': 'five stone', 'deco': 'art deco', 'antique': 'vintage',
    'edwardian': 'vintage', 'victorian': 'vintage', 'two-tone': 'two tone',
    'botanical': 'nature', 'leaf': 'nature', 'leaves': 'nature', 'vine': 'nature',
    'flower': 'floral', 'colored diamond': 'fancy color', 'colored stone': 'fancy color',
    'gemstone accent': 'fancy color', 'monogram': 'hand engrav', 'half pav\u00e9': 'half pave',
    'tension set': 'tension', 'tension setting': 'tension', 'floating': 'tension',
    'beaded edge': 'milgrain', 'millegrain': 'milgrain', 'scrollwork': 'filigree',
    'lace': 'filigree', 'inscription': 'engrav', 'inscribe': 'engrav',
    'accent stones': 'side stones', 'accent diamonds': 'side stones', 'shoulder stones': 'side stones',
    'you and me': 'toi et moi', 'two stone': 'toi et moi'
  },
  presetEquivalents: ['halo', 'pave', 'three stone', 'cathedral'],
  stackDamping: { fullCount: 2, extraWeight: 0.75 },
  minor: { imageEachAfter: 2, imageAdd: 50, imageCap: 200, richDescChars: 400, richDescAdd: 150 },
  uniqueness: { two: 1.15, three: 1.22, fourPlus: 1.30 },
  rangeSpread: 0.08,
  roundTo: 50
};

document.addEventListener('alpine:init', () => {
  Alpine.data('adRingConfigurator', () => ({

    /* ── AI Custom Design — state ─────────────────────────────────────────── */
    finalTweaks: '',
    customDesign: {
      active: false, dragOver: false, images: [], imagePreviews: [], imagesB64: [],
      description: '', occasion: '', deadline: '', budget: '',
      name: '', email: '', phone: '', company: '',
      priced: false,
      estimate: { low: 0, high: 0, point: 0, features: [], breakdown: '' },
      submitting: false, submitted: false,
      errors: { images: '', description: '', name: '', email: '', form: '' }
    },

    /* ── AI Custom Design — methods ───────────────────────────────────────── */
    get CUSTOM_MAX_IMAGES() { return 6; },
    get CUSTOM_MAX_BYTES() { return 10 * 1024 * 1024; },
    get CUSTOM_ALLOWED_MIME() { return ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp']; },

    selectCustomDesign() {
      this.selectedSetting = 'custom-ai';
      this.customDesign.active = true;
      this.$nextTick(() => {
        const panel = document.getElementById('ad-rc-cd-panel');
        if (panel && panel.scrollIntoView) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },

    handleCustomImages(event) {
      this._addCustomImages(event.target.files);
      event.target.value = '';
      this.customDesign.priced = false;
    },

    handleCustomDrop(event) {
      this.customDesign.dragOver = false;
      if (event.dataTransfer && event.dataTransfer.files) this._addCustomImages(event.dataTransfer.files);
      this.customDesign.priced = false;
    },

    _addCustomImages(fileList) {
      const incoming = Array.from(fileList || []);
      const errs = [];
      for (const file of incoming) {
        if (this.customDesign.images.length >= this.CUSTOM_MAX_IMAGES) { errs.push('Up to ' + this.CUSTOM_MAX_IMAGES + ' images.'); break; }
        if (this.CUSTOM_ALLOWED_MIME.indexOf(file.type) === -1) { errs.push(file.name + ' isn\u2019t a supported format.'); continue; }
        if (file.size > this.CUSTOM_MAX_BYTES) { errs.push(file.name + ' is over 10MB.'); continue; }
        this.customDesign.images.push(file);
        this.customDesign.imagePreviews.push(URL.createObjectURL(file));
        (function (self, idx, f) {
          self._downscaleToDataURL(f)
            .then(function (b64) { self.customDesign.imagesB64[idx] = b64; })
            .catch(function () { self.customDesign.imagesB64[idx] = ''; });
        })(this, this.customDesign.images.length - 1, file);
      }
      this.customDesign.errors.images = errs.join(' ');
    },

    removeCustomImage(index) {
      const url = this.customDesign.imagePreviews[index];
      if (url) URL.revokeObjectURL(url);
      this.customDesign.images.splice(index, 1);
      this.customDesign.imagePreviews.splice(index, 1);
      this.customDesign.imagesB64.splice(index, 1);
      this.customDesign.priced = false;
    },

    onCustomDescriptionInput() { this.customDesign.priced = false; },

    _detectFeatures(text) {
      const hay = ' ' + (text || '').toLowerCase() + ' ';
      const found = new Map();
      const consider = (canonical) => {
        if (found.has(canonical)) return;
        if (PRICING.structural[canonical] != null) found.set(canonical, { type: 'structural', add: PRICING.structural[canonical] });
        else if (PRICING.detailing[canonical] != null) found.set(canonical, { type: 'detailing', add: PRICING.detailing[canonical] });
      };
      Object.keys(PRICING.structural).forEach((k) => { if (hay.indexOf(k) !== -1) consider(k); });
      Object.keys(PRICING.detailing).forEach((k) => { if (hay.indexOf(k) !== -1) consider(k); });
      Object.keys(PRICING.synonyms).forEach((syn) => { if (hay.indexOf(syn) !== -1) consider(PRICING.synonyms[syn]); });
      const keys = Array.from(found.keys());
      keys.forEach((k) => { if (keys.some((other) => other !== k && other.indexOf(k) !== -1)) found.delete(k); });
      return Array.from(found, ([key, v]) => ({ key, type: v.type, add: v.add }));
    },

    /* Wired to the LIVE metal: uses selectedMetalInfo.price as the real adder */
    _metalConfig() {
      const info = this.selectedMetalInfo;
      return { add: (info && Number(info.price)) || 0, mult: 1.0 };
    },

    _roundTo(n, step) { return Math.round(n / step) * step; },

    _computeEstimate() {
      const cd = this.customDesign;
      const features = this._detectFeatures(cd.description);
      const adds = features.map((f) => f.add).sort((a, b) => b - a);
      let featureSubtotal = 0;
      adds.forEach((v, i) => { featureSubtotal += (i < PRICING.stackDamping.fullCount) ? v : v * PRICING.stackDamping.extraWeight; });
      const extraImages = Math.max(cd.images.length - PRICING.minor.imageEachAfter, 0);
      const imageAdd = Math.min(extraImages * PRICING.minor.imageAdd, PRICING.minor.imageCap);
      const richAdd = (cd.description.trim().length > PRICING.minor.richDescChars) ? PRICING.minor.richDescAdd : 0;
      let subtotal = PRICING.base + featureSubtotal + imageAdd + richAdd;
      const m = this._metalConfig();
      subtotal = subtotal * m.mult + m.add;
      const distinct = features.length;
      const structuralOnly = features.filter((f) => f.type === 'structural');
      const mapsToPreset = distinct === 1 && structuralOnly.length === 1 && PRICING.presetEquivalents.indexOf(structuralOnly[0].key) !== -1;
      let uniq = 1.0;
      if (!mapsToPreset) {
        if (distinct === 2) uniq = PRICING.uniqueness.two;
        else if (distinct === 3) uniq = PRICING.uniqueness.three;
        else if (distinct >= 4) uniq = PRICING.uniqueness.fourPlus;
      }
      const point = this._roundTo(subtotal * uniq, PRICING.roundTo);
      const low = this._roundTo(point * (1 - PRICING.rangeSpread), PRICING.roundTo);
      const high = this._roundTo(point * (1 + PRICING.rangeSpread), PRICING.roundTo);
      const featureNames = features.map((f) => f.key).join(', ') || 'classic / minimal';
      return {
        low, high, point,
      mid: this._roundTo((low + high) / 2, 25),
      features: features.map((f) => f.key),
        breakdown: 'base $' + PRICING.base + ' + features ($' + Math.round(featureSubtotal) + ')' +
          (imageAdd || richAdd ? ' + detail' : '') + ' \u00b7 metal' + (m.add ? ' +$' + m.add : '') +
          ' \u00b7 uniqueness \u00d7' + uniq + ' \u00b7 features: ' + featureNames
      };
    },

    priceCustomDesign() {
      const cd = this.customDesign;
      cd.errors.images = ''; cd.errors.description = '';
      let ok = true;
      if (cd.images.length < 1) { cd.errors.images = 'Add at least one inspiration image first.'; ok = false; }
      if (cd.description.trim().length < 30) { cd.errors.description = 'Tell us a bit more so we can price it accurately (30+ characters).'; ok = false; }
      if (!ok) return;
      cd.estimate = this._computeEstimate();
      cd.priced = true;
      this.$nextTick(() => {
        const el = document.getElementById('ad-rc-cd-estimate');
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },

    get customEstimatedTotalLow() { return (Number(this.totalPrice) || 0) + (this.customDesign.estimate.low || 0); },
    get customEstimatedTotalHigh() { return (Number(this.totalPrice) || 0) + (this.customDesign.estimate.high || 0); },
    get customEstimatedTotalMid() { return this._roundTo((Number(this.totalPrice) || 0) + (this.customDesign.estimate.mid || 0), 100); },

    _validateCustomDesign() {
      const cd = this.customDesign;
      cd.errors = { images: '', description: '', name: '', email: '', form: '' };
      let ok = true;
      if (cd.images.length < 1) { cd.errors.images = 'Please add at least one inspiration image.'; ok = false; }
      if (cd.description.trim().length < 30) { cd.errors.description = 'A few more details will help \u2014 try for 30+ characters.'; ok = false; }
      if (cd.name.trim().length < 2) { cd.errors.name = 'Please tell us your name.'; ok = false; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cd.email.trim())) { cd.errors.email = 'Please enter a valid email.'; ok = false; }
      return ok;
    },

    /* Real-click submit on a visible {% form 'contact' %} so Shopify's reCAPTCHA
       attaches its token. A plain fetch (or programmatic submit on a hidden form)
       is rejected with "Missing CAPTCHA token". Synchronous: do not await here —
       images are pre-encoded to base64 at upload time. */
    onCustomSubmitClick(e) {
      const cd = this.customDesign;
      if (cd.company) { e.preventDefault(); cd.submitted = true; return; }   // honeypot
      if (!this._validateCustomDesign()) { e.preventDefault(); return; }
      if (!cd.priced) { cd.estimate = this._computeEstimate(); cd.priced = true; }

      const form = (e.target && e.target.form) || document.getElementById('ad-rc-cd-contact-form');
      if (!form) { e.preventDefault(); cd.errors.form = 'Form unavailable. Please email dave@atelierdiamante.com directly.'; return; }
      const set = (k, v) => { const el = form.querySelector('[data-cd="' + k + '"]'); if (el) el.value = (v == null ? '' : String(v)); };

      const stoneShape = (this.selectedDiamond && this.selectedDiamond.shape) || this.selectedShape || '';
      const stoneCarat = (this.selectedDiamond && this.selectedDiamond.carat) || '';
      const stoneMetal = (this.selectedMetalInfo && this.selectedMetalInfo.label) || this.selectedMetal || '';
      const quoted = '$' + ((cd.estimate.mid || 0)).toLocaleString();

      set('inqtype', 'Begin Your Design (in-configurator)');
      set('from', '/pages/design-your-ring');
      set('shape', stoneShape);
      set('carat', stoneCarat ? String(stoneCarat) + ' ct' : '');
      set('metal', stoneMetal);
      set('stoneid', (this.selectedDiamond && this.selectedDiamond.stoneId) || '');
      set('variant', (this.selectedDiamond && this.selectedDiamond.variantId) || '');
      set('imgcount', String(cd.images.length));
      set('quoted', quoted);
      set('band', '$' + cd.estimate.low + '\u2013$' + cd.estimate.high + ' (internal; customer sees ' + quoted + ')');
      set('features', (cd.estimate.features || []).join(', ') || '(none detected)');
      set('breakdown', cd.estimate.breakdown || '');
      set('tweaks', (this.finalTweaks || '').trim());
      set('body',
        'In-configurator custom design request.\n\n' +
        'Stone: ' + (stoneShape || '?') + ' / ' + (stoneCarat || '?') + 'ct / ' + (stoneMetal || '?') + '\n' +
        'Quoted setting price (shown to customer): ' + quoted + '\n' +
        'Internal price band: $' + cd.estimate.low + '\u2013$' + cd.estimate.high + '\n' +
        'Detected features: ' + ((cd.estimate.features || []).join(', ') || 'none') + '\n' +
        'Occasion: ' + (cd.occasion || '-') + ' / Deadline: ' + (cd.deadline || '-') + ' / Budget: ' + (cd.budget || 'discuss') + '\n' +
        'Inspiration images uploaded by customer: ' + cd.images.length + ' (request them in your reply)\n' +
        ((this.finalTweaks || '').trim() ? ('Final adjustments: ' + this.finalTweaks.trim() + '\n') : '') +
        '\nDescription:\n' + cd.description.trim()
      );
      set('imgsnote', cd.images.length + ' image(s) uploaded by the customer \u2014 request them in your reply.');

      try {
        if (window._learnq) {
          window._learnq.push(['identify', { $email: cd.email.trim(), $first_name: cd.name.trim().split(' ')[0], $last_name: cd.name.trim().split(' ').slice(1).join(' ') }]);
          window._learnq.push(['track', 'ai_setting_design_submitted', {
            chosen_shape: stoneShape || null, chosen_carat: stoneCarat || null, chosen_metal: stoneMetal || null,
            occasion: cd.occasion || null, budget_range: cd.budget || null, image_count: cd.images.length,
            quoted_price: cd.estimate.mid, estimate_low: cd.estimate.low, estimate_high: cd.estimate.high,
            detected_features: cd.estimate.features
          }]);
        }
      } catch (err) { /* analytics never blocks submit */ }

      try { sessionStorage.setItem('ad_cd_submitted', '1'); } catch (err) {}
      // NOTE: do NOT set cd.submitting/disable the button synchronously here —
      // a disabled submitter cancels the native form submit. Let it post.
      // No preventDefault — native Shopify contact-form submit proceeds (reCAPTCHA attaches token).
    },

    /* Downscale a File to a small JPEG data URL for emailable inspiration refs */
    _downscaleToDataURL(file) {
      return new Promise((resolve, reject) => {
        try {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.onload = () => {
            const MAX = 700;
            let w = img.width, h = img.height;
            if (w > MAX || h > MAX) { const s = MAX / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            URL.revokeObjectURL(url);
            try { resolve(canvas.toDataURL('image/jpeg', 0.55)); } catch (e) { reject(e); }
          };
          img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
          img.src = url;
        } catch (e) { reject(e); }
      });
    },


    /* ---- Image + mobile-CTA helpers (Path A1 restore) ------------------ */
    /* Re-added: template binds these but they went missing post-clobber.   */
    diamondImageSrc(diamond) {
      if (diamond && diamond.image) return diamond.image;
      if (diamond && diamond.shape) {
        return shapeImgSrc(String(diamond.shape).toLowerCase());
      }
      return shapeImgSrc('round');
    },

    modalSpinImage() {
      const d = this.previewDiamond || this.selectedDiamond;
      if (d) {
        if (d.images && d.images.length > 1) {
          const idx = this.spinFrameIndex || 0;
          return d.images[idx] || d.images[0];
        }
        if (d.image) return d.image;
        if (d.shape) return shapeImgSrc(String(d.shape).toLowerCase());
      }
      return this.previewImage || shapeImgSrc(this.selectedShape || 'round');
    },

    get mobileCtaDisabled() {
      if (this.addingToCart) return true;
      if (this.currentStep === this.totalSteps) {
        return !this.selectedDiamond || !this.selectedSetting || !this.selectedMetal;
      }
      return !this.canProceed;
    },

    get mobileCtaLabel() {
      if (this.addingToCart) return 'Adding\u2026';
      if (this.currentStep === this.totalSteps) return 'Add to Cart';
      return 'Continue';
    },

    /* ---- Lifecycle ------------------------------------------------------ */
    /**
     * Read URL params from mega menu deep links and preselect.
     * Supported params: ?shape=Round  ?setting=halo  ?metal=platinum
     * If a setting or metal is provided, jump straight to Step 4.
     */
    init() {
      /* Live match count: recompute when any preference changes */
      ['selectedShape', 'colorType', 'caratMin', 'caratMax', 'budgetMin', 'budgetMax', 'qualityTier']
        .forEach(k => this.$watch(k, () => this.recountMatches()));

      // AI custom design: show confirmation after the native contact-form reload
      try {
        if (sessionStorage.getItem('ad_cd_submitted') === '1' &&
            new URL(window.location).searchParams.get('contact_posted') === 'true') {
          sessionStorage.removeItem('ad_cd_submitted');
          this.selectedSetting = 'custom-ai';
          this.customDesign.active = true;
          this.customDesign.submitted = true;
          this.currentStep = 4;
        }
      } catch (e) {}
      const sp = new URL(window.location).searchParams;

      const shape = sp.get('shape');
      if (shape) {
        const key = shape.toLowerCase();
        if (this.shapes.find(s => s.key === key)) {
          this.selectShape(key);
        }
      }

      const setting = sp.get('setting');
      if (setting && SETTINGS.find(s => s.key === setting)) {
        this.selectedSetting = setting;
      }

      const metal = sp.get('metal');
      if (metal && METALS.find(m => m.key === metal)) {
        this.selectedMetal = metal;
      }

      /* If user landed via Style/Metal link, jump to the Setting step */
      if (setting || metal) {
        this.currentStep    = 4;
        this.maxReachedStep = 4;
      }
    },

    /* ---- Step machine --------------------------------------------------- */
    currentStep: 1,
    totalSteps:  6,
    maxReachedStep: 1,

    /* ---- Step 1: Shape -------------------------------------------------- */
    selectedShape: null,
    shapes: Object.entries(SHAPE_INFO).map(([key, info]) => ({
      key,
      label: info.label,
      tagline: info.tagline,
      svg: shapeImgSrc(key),
    })),
    fancyColors: Object.entries(FANCY_INFO).map(([hue, info]) => ({
      hue,
      label: info.label,
      tagline: info.tagline,
      img: fancyImgSrc(hue),
    })),

    /* ---- Step 2: Preferences -------------------------------------------- */
    colorType: 'colorless', /* 'colorless' | 'fancy' | 'all' */
    fancyHue: '', /* '' | yellow | pink | blue | red | green */
    caratRange: [0.5, 50],
    budgetRange: [1000, 200000],
    caratMin: 0.5,
    caratMax: 50,
    budgetMin: 1000,
    budgetMax: 200000,
    caratBand: '',        /* preset chip key; '' = any, 'custom' = sliders */
    budgetBand: '',
    qualityTier: '',      /* '' | 'heirloom' | 'sweetspot' | 'presence' */
    showCustomCarat: false,
    showCustomBudget: false,
    matchCount: null,     /* live count for the Preferences step */

    /* ---- Step 3: Diamond selection -------------------------------------- */
    diamonds: [],
    diamondLoading: false,
    diamondError: null,
    selectedDiamond: null,
    showCount: 12,
    sortBy: 'price-asc',

    /* ---- Step 4: Setting + Metal ---------------------------------------- */
    selectedSetting: null,
    selectedMetal: null,
    settings: SETTINGS,
    metals: METALS,

    /* ---- Step 5: Inspiration (optional — shared across all settings) ----- */
    inspirationUrl:      '',    /* reference link (Pinterest, Instagram, etc.) */
    inspirationNotes:    '',    /* free-text vision description */
    inspirationFiles:    [],    /* File[] from <input type=file> */
    inspirationPreviews: [],    /* { name, size, dataUrl } for thumbnails */
    inspirationError:    '',
    inspirationMaxFiles: 5,
    inspirationMaxBytes: 10 * 1024 * 1024,  /* 10MB per file */

    /* ---- Step 5: Craftsmanship level ------------------------------------- */
    intricacyLevel:   'standard',   /* 'standard' | 'enhanced' | 'bespoke' */
    intricacyOptions: INTRICACY_OPTIONS,

    /* ---- Step 5: Personalize (size + engraving) ------------------------- */
    ringSize: '',
    sizeUnknown: false,
    ringSizes: ['4','4.5','5','5.5','6','6.5','7','7.5','8','8.5','9','9.5','10','10.5','11','11.5','12','12.5','13'],
    engravingText: '',
    engravingFont: 'serif',
    engravingFonts: [
      { id: 'serif',  label: 'Serif',  preview: 'Aa', css: "'Cormorant Garamond', Georgia, serif" },
      { id: 'sans',   label: 'Sans',   preview: 'Aa', css: "'Jost', system-ui, sans-serif" },
      { id: 'script', label: 'Script', preview: 'Aa', css: "'Pinyon Script', 'Cormorant Garamond', cursive" },
    ],

    /* ---- Step 6: Review + Cart ------------------------------------------ */
    addingToCart: false,
    cartSuccess: false,
    cartError: null,

    /* ---- Preview state -------------------------------------------------- */
    previewImage: null,

    /* ---- 360° spin viewer state ----------------------------------------- */
    spinFrameIndex: 0,
    spinDragging: false,
    spinDragStartX: 0,
    spinDragStartFrame: 0,

    /* ---- Diamond inspection panel --------------------------------------- */
    previewDiamond: null,
    viewerUrl: null,       /* pkonline iframe URL for active stone */
    viewerFading: false,   /* triggers CSS fade between stone switches */
    viewer360Ready: false, /* poster covers until the 13MB pkonline iframe finishes loading */

    /* ================================================================
       COMPUTED
       ================================================================ */

    get isStepComplete() {
      return {
        1: !!this.selectedShape,
        2: true, /* preferences always have defaults */
        3: !!this.selectedDiamond,
        4: !!(this.selectedSetting && this.selectedMetal),
        5: !!(this.ringSize || this.sizeUnknown),
        6: false,
      };
    },

    get ringSizeLabel() {
      if (this.ringSize) return 'US ' + this.ringSize;
      if (this.sizeUnknown) return 'To be confirmed with Dave';
      return '';
    },

    get canProceed() {
      return this.isStepComplete[this.currentStep];
    },

    get progress() {
      return Math.round(((this.currentStep - 1) / (this.totalSteps - 1)) * 100);
    },

    get selectedDiamondFormatted() {
      if (!this.selectedDiamond) return null;
      const d = this.selectedDiamond;
      return {
        ...d,
        priceFormatted: _money(d.price),
        specs: `${d.carat}ct ${d.shape} ${d.color}/${d.clarity}`,
      };
    },

    get selectedSettingInfo() {
      return SETTINGS.find(s => s.key === this.selectedSetting) || null;
    },

    get selectedMetalInfo() {
      return METALS.find(m => m.key === this.selectedMetal) || null;
    },

    get intricacyPrice() {
      const opt = INTRICACY_OPTIONS.find(o => o.key === this.intricacyLevel);
      return opt ? opt.price : 0;
    },

    get totalPrice() {
      if (!this.selectedDiamond) return null;
      const settingPrice   = this.selectedSettingInfo?.price || 0;
      const metalPrice     = this.selectedMetalInfo?.price   || 0;
      return this.selectedDiamond.price + settingPrice + metalPrice;
    },

    get totalPriceFormatted() {
      return this.totalPrice ? _money(this.totalPrice) : null;
    },

    get sortedDiamonds() {
      const arr = [...this.diamonds];
      switch (this.sortBy) {
        case 'price-asc':  arr.sort((a, b) => a.price - b.price); break;
        case 'price-desc': arr.sort((a, b) => b.price - a.price); break;
        case 'carat-desc': arr.sort((a, b) => b.carat - a.carat); break;
        case 'carat-asc':  arr.sort((a, b) => a.carat - b.carat); break;
      }
      return arr;
    },

    get visibleDiamonds() {
      return this.sortedDiamonds.slice(0, this.showCount);
    },

    get hasMoreDiamonds() {
      return this.sortedDiamonds.length > this.showCount;
    },

    get mobileSummary() {
      const parts = [];
      if (this.selectedShape) parts.push(SHAPE_INFO[this.selectedShape]?.label);
      if (this.selectedDiamond) parts.push(this.selectedDiamond.carat + 'ct');
      if (this.selectedSetting) parts.push(this.selectedSettingInfo?.label);
      if (this.selectedMetal) parts.push(this.selectedMetalInfo?.label);
      if (this.ringSize) parts.push('Size ' + this.ringSize);
      return parts.join(' · ') || 'Design your ring';
    },

    /* ================================================================
       STEP NAVIGATION
       ================================================================ */

    goToStep(step) {
      if (step < 1 || step > this.totalSteps) return;
      if (step > this.maxReachedStep + 1) return; /* Can't skip ahead */
      this.currentStep = step;
      if (step === 2) this.prefetchCatalog();
      this._scrollToTop();
    },

    nextStep() {
      if (!this.canProceed) return;
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
        if (this.currentStep > this.maxReachedStep) {
          this.maxReachedStep = this.currentStep;
        }
        /* Prefetch catalog for the live match count on Preferences */
        if (this.currentStep === 2) this.prefetchCatalog();
        /* Auto-fetch diamonds when entering step 3 */
        if (this.currentStep === 3 && this.diamonds.length === 0) {
          this.loadDiamonds();
        }
        this._scrollToTop();
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this._scrollToTop();
      }
    },

    _scrollToTop() {
      const el = document.getElementById(`ad-rc-top-${window.AD_RC?.sectionId}`);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    },

    /* ================================================================
       STEP 1: SHAPE SELECTION
       ================================================================ */

    selectShape(key) {
      this.selectedShape = key;
      /* Reset downstream selections when shape changes */
      this.selectedDiamond = null;
      this.diamonds = [];
      this.showCount = 12;
      this.previewImage = shapeImgSrc(key);
    },

    /* ================================================================
       STEP 2: PREFERENCES (sliders update state)
       ================================================================ */

    updateCaratMin(val) { this.caratMin = parseFloat(val); this.caratBand = 'custom'; },
    updateCaratMax(val) { this.caratMax = parseFloat(val); this.caratBand = 'custom'; },
    updateBudgetMin(val) { this.budgetMin = parseInt(val); this.budgetBand = 'custom'; },
    updateBudgetMax(val) { this.budgetMax = parseInt(val); this.budgetBand = 'custom'; },

    setCaratBand(key, min, max) {
      this.caratBand = key;
      this.caratMin = min;
      this.caratMax = max;
      this.showCustomCarat = false;
    },
    setBudgetBand(key, min, max) {
      this.budgetBand = key;
      this.budgetMin = min;
      this.budgetMax = max;
      this.showCustomBudget = false;
    },
    setQualityTier(tier) { this.qualityTier = this.qualityTier === tier ? '' : tier; },
    /* Real setting-tile photo for a setting card, reactive to selected metal + stone shape.
       Lookup: AD_SETTING_IMGS[setting][metalTone][shape]; falls back tone -> white-gold -> any,
       shape -> round -> any; returns null when no photo exists (card keeps its SVG glyph). */
    settingImg(key) {
      const mk = (this.selectedMetal || '').toLowerCase();
      let tone = 'white-gold';
      if (mk.indexOf('yellow') > -1) tone = 'yellow-gold';
      else if (mk.indexOf('rose') > -1) tone = 'rose-gold';
      else if (mk.indexOf('platinum') > -1) tone = 'platinum';
      const shape = ((this.selectedDiamond && this.selectedDiamond.shape) || this.selectedShape || 'round').toLowerCase();
      /* Fancy-color ring renders take priority when a fancy hue is chosen; fall back to white tiles */
      if (this.colorType === 'fancy' && this.fancyHue) {
        const fset = ((window.AD_SETTING_FANCY || {})[this.fancyHue] || {})[key];
        if (fset) {
          const fTone = fset[tone] || fset['white-gold'] || Object.values(fset)[0];
          if (fTone) {
            const fHit = fTone[shape] || fTone['round'] || Object.values(fTone)[0];
            if (fHit) return fHit;
          }
        }
      }
      const imgs = (window.AD_SETTING_IMGS || {})[key];
      if (!imgs) return null;
      const byTone = imgs[tone] || imgs['white-gold'] || Object.values(imgs)[0];
      if (!byTone) return null;
      return byTone[shape] || byTone['round'] || Object.values(byTone)[0] || null;
    },
    toggleCustomCarat() {
      this.showCustomCarat = !this.showCustomCarat;
      if (this.showCustomCarat) this.caratBand = 'custom';
    },
    toggleCustomBudget() {
      this.showCustomBudget = !this.showCustomBudget;
      if (this.showCustomBudget) this.budgetBand = 'custom';
    },
    recountMatches() {
      if (!_adCatalog) { this.matchCount = null; return; }
      this.matchCount = adFilterDiamonds(_adCatalog, {
        shape:       this.selectedShape,
        colorType:   this.colorType,
        fancyHue:    this.fancyHue,
        caratMin:    this.caratMin,
        caratMax:    this.caratMax,
        budgetMin:   this.budgetMin,
        budgetMax:   this.budgetMax,
        qualityTier: this.qualityTier,
      }).length;
    },
    prefetchCatalog() {
      const cfg = window.AD_RC;
      if (!cfg?.collectionHandle) return;
      if (_adCatalog) { this.recountMatches(); return; }
      fetchDiamonds(cfg.collectionHandle, {})
        .then(() => this.recountMatches())
        .catch(() => {});
    },
    selectColorType(type) {
      this.colorType = type;
      /* Quality tiers use colorless grading; clear when switching to fancy */
      if (type === 'fancy') this.qualityTier = '';
      if (type !== 'fancy') this.fancyHue = '';
      /* Reset diamond selection if color type changes */
      this.diamonds = [];
      this.selectedDiamond = null;
      this.showCount = 12;
      this.recountMatches();
    },
    selectFancyHue(hue) {
      this.fancyHue = this.fancyHue === hue ? '' : hue;
      this.diamonds = [];
      this.selectedDiamond = null;
      this.showCount = 12;
      this.recountMatches();
    },

    /* ================================================================
       STEP 3: LOAD + SELECT DIAMONDS
       ================================================================ */

    async loadDiamonds() {
      const cfg = window.AD_RC;
      if (!cfg?.collectionHandle) {
        this.diamondError = 'Diamond collection not configured.';
        return;
      }

      this.diamondLoading = true;
      this.diamondError = null;
      this.showCount = 12;

      try {
        const raw = await fetchDiamonds(cfg.collectionHandle, {
          shape:     this.selectedShape,
          colorType: this.colorType,
          fancyHue:  this.fancyHue,
          caratMin:  this.caratMin,
          caratMax:  this.caratMax,
          budgetMin: this.budgetMin,
          budgetMax: this.budgetMax,
          qualityTier: this.qualityTier,
        });

        this.diamonds = raw.map(parseDiamond).filter(d => d.available);

        /* Maximum Presence: largest stones first */
        if (this.qualityTier === 'presence') {
          this.diamonds.sort((a, b) => (b.carat || 0) - (a.carat || 0));
        }

        /* Clear a stale preview if it no longer belongs to the current results */
        if (this.previewDiamond && !this.diamonds.some(d => d.id === this.previewDiamond.id)) {
          this.previewDiamond = null;
        }

        if (this.diamonds.length === 0) {
          this.diamondError = 'No diamonds match your preferences. Try adjusting your carat range or budget.';
        } else if (!this.previewDiamond) {
          // Auto-load the first stone into the inspection panel
          this.inspectDiamond(this.diamonds[0]);
        }
      } catch (err) {
        console.error('[AD Configurator]', err);
        this.diamondError = 'Unable to load diamonds. Please refresh and try again.';
      } finally {
        this.diamondLoading = false;
      }
    },

    selectDiamond(diamond) {
      this.selectedDiamond = diamond;
      this.spinFrameIndex = 0; /* reset to first frame on new selection */
      if (diamond.image) {
        this.previewImage = diamond.image;
      }
    },

    /* ================================================================
       360° SPIN VIEWER — drag/touch to spin through product images
       ================================================================ */

    spinCurrentImage() {
      // Uses previewDiamond — the stone being inspected in the left panel
      const frames = this.previewDiamond?.images;
      if (!frames || frames.length === 0) return this.previewDiamond?.image || null;
      return frames[this.spinFrameIndex] || frames[0];
    },

    spinFrameCount() {
      return this.previewDiamond?.images?.length || 0;
    },

    spinStart(e) {
      if (this.spinFrameCount() <= 1) return;
      this.spinDragging = true;
      this.spinDragStartX = (e.touches ? e.touches[0].clientX : e.clientX);
      this.spinDragStartFrame = this.spinFrameIndex;
      e.preventDefault();
    },

    spinMove(e) {
      if (!this.spinDragging) return;
      const x = (e.touches ? e.touches[0].clientX : e.clientX);
      const delta = x - this.spinDragStartX;
      const frameCount = this.spinFrameCount();
      /* ~4px per frame feels natural */
      const frameDelta = Math.round(delta / 4);
      this.spinFrameIndex = ((this.spinDragStartFrame - frameDelta) % frameCount + frameCount) % frameCount;
      e.preventDefault();
    },

    spinEnd() {
      this.spinDragging = false;
    },

    /* ================================================================
       DIAMOND QUICK-VIEW MODAL
       ================================================================ */

    /* inspectDiamond — click a card to load it into the left inspection panel.
       No modal. previewDiamond drives the viewer; selectedDiamond is confirmed only.
       Uses pkonline iframe when stoneId is available; spin viewer as fallback. */
    inspectDiamond(diamond) {
      const cdnBase = (window.AD_RC || {}).cdnBase || 'https://pkonline.blob.core.windows.net/pkonline';
      const newUrl  = diamond?.stoneId ? `${cdnBase}/${diamond.stoneId}.html` : null;

      /* Brief fade between stones for a luxury feel */
      if (newUrl && newUrl !== this.viewerUrl) {
        this.viewerFading = true;
        setTimeout(() => {
          this.previewDiamond = diamond;
          this.viewerUrl      = newUrl;
          this.viewer360Ready = false;
          this.spinFrameIndex = 0;
          this.spinDragging   = false;
          /* fade back in after the iframe src has a moment to update */
          setTimeout(() => { this.viewerFading = false; }, 80);
        }, 120);
      } else {
        this.previewDiamond = diamond;
        this.viewerUrl      = newUrl;
        this.viewer360Ready = false;
        this.spinFrameIndex = 0;
        this.spinDragging   = false;
        this.viewerFading   = false;
      }
    },

    /* Confirm the currently inspected stone and advance */
    confirmPreviewedStone() {
      if (!this.previewDiamond) return;
      this.selectDiamond(this.previewDiamond);
      if (this.currentStep === 3) this.nextStep();
    },

    showMoreDiamonds() {
      this.showCount += 12;
    },

    refreshDiamonds() {
      this.diamonds = [];
      this.selectedDiamond = null;
      this.loadDiamonds();
    },

    /* ================================================================
       STEP 4: SETTING + METAL
       ================================================================ */

    selectSetting(key) {
      this.selectedSetting = key;
    },

    selectMetal(key) {
      this.selectedMetal = key;
    },

    /* ================================================================
       STEP 5: INSPIRATION UPLOAD (available for all settings)
       ================================================================ */

    onInspirationFiles(event) {
      const self = this;
      const incoming = Array.from(event.target.files || []);
      this.inspirationError = '';

      incoming.forEach(function (file) {
        if (self.inspirationFiles.length >= self.inspirationMaxFiles) {
          self.inspirationError = 'Maximum ' + self.inspirationMaxFiles + ' files. Remove one to add another.';
          return;
        }
        if (file.size > self.inspirationMaxBytes) {
          self.inspirationError = file.name + ' is too large (max 10MB per file).';
          return;
        }
        if (!/^image\//i.test(file.type)) {
          self.inspirationError = file.name + ' is not an image. Please upload JPG, PNG, or HEIC files.';
          return;
        }
        self.inspirationFiles.push(file);
        const reader = new FileReader();
        reader.onload = function (e) {
          self.inspirationPreviews.push({
            name: file.name,
            size: (file.size / 1024).toFixed(0) + ' KB',
            dataUrl: e.target.result,
          });
        };
        reader.readAsDataURL(file);
      });
      event.target.value = '';
    },

    removeInspirationFile(index) {
      this.inspirationFiles.splice(index, 1);
      this.inspirationPreviews.splice(index, 1);
      this.inspirationError = '';
    },

    /* ================================================================
       STEP 5: PERSONALIZE (size + engraving)
       ================================================================ */

    selectRingSize(size) {
      this.ringSize = size;
      this.sizeUnknown = false;
    },

    onSizeUnknownToggle() {
      if (this.sizeUnknown) {
        this.ringSize = '';
      }
    },

    /* ================================================================
       STEP 6: ADD TO CART (or submit quote for Custom Design)
       ================================================================ */

    async addToCart() {
      if (this.addingToCart || !this.selectedDiamond?.variantId) return;
      this.addingToCart = true;
      this.cartError = null;

      try {
        const properties = {
          ...(this.finalTweaks && this.finalTweaks.trim() ? { 'Final Adjustments': this.finalTweaks.trim() } : {}),
          'Setting Style':      this.selectedSettingInfo?.label || '',
          'Metal':              this.selectedMetalInfo?.label   || '',
          'Ring Size':          this.sizeUnknown ? 'TBD - sized at consultation' : this.ringSizeLabel,
          'Diamond':            this.selectedDiamond.stoneId,
          'Shape':              this.selectedDiamond.shape,
          'Carat':              String(this.selectedDiamond.carat) + ' ct',
          'Color':              this.selectedDiamond.color,
          'Clarity':            this.selectedDiamond.clarity,
        };
        if (this.engravingText) {
          properties['Engraving']      = this.engravingText;
          properties['Engraving Font'] = this.engravingFont;
        }
        if (this.inspirationUrl) {
          properties['Inspiration Reference'] = this.inspirationUrl;
        }
        if (this.inspirationNotes.trim()) {
          properties['Inspiration Notes'] = this.inspirationNotes.trim();
        }
        if (this.inspirationFiles.length > 0) {
          properties['Inspiration Photos'] = this.inspirationFiles.map(function (f) { return f.name; }).join(', ');
        }

        const items = [{
          id:         parseInt(this.selectedDiamond.variantId, 10),
          quantity:   1,
          properties: properties,
        }];

        /* Charge the setting + metal via the hidden "The Setting" product. Cart
           line-item properties are display-only and never affect price, so the
           setting must ride as a real variant. Throw rather than add an underpriced cart. */
        if (this.selectedSetting && this.selectedMetal) {
          if (!window.AD_SettingVariants) {
            const sres = await fetch('/products/ad-ring-setting.js');
            if (sres.ok) {
              const sp = await sres.json();
              window.AD_SettingVariants = {};
              (sp.variants || []).forEach(function (v) { if (v.sku) { window.AD_SettingVariants[v.sku] = v.id; } });
            }
          }
          const skuKey = 'SET|' + this.selectedSetting + '|' + this.selectedMetal;
          const settingVid = window.AD_SettingVariants && window.AD_SettingVariants[skuKey];
          if (!settingVid) {
            throw new Error("We couldn't price your setting just now. Please refresh and try again, or contact us.");
          }
          items.push({
            id:         settingVid,
            quantity:   1,
            properties: {
              'For Diamond': this.selectedDiamond.stoneId,
              'Ring Size':   this.sizeUnknown ? 'TBD - sized at consultation' : this.ringSizeLabel,
            },
          });
        }

        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body:    JSON.stringify({ items: items }),
        });

        if (!res.ok) {
          const errBody = await res.json().catch(function () { return {}; });
          throw new Error(errBody.description || errBody.message || 'Cart add failed (' + res.status + ')');
        }

        /* Redirect directly to cart — no drawer barrier */
        window.location.href = '/cart';
      } catch (err) {
        console.error('Ring Configurator: addToCart failed -', err);
        this.cartError = err.message || "Sorry, we couldn't add this to your cart. Please try again or contact us.";
        this.addingToCart = false;
      }
    },

    /* ================================================================
       MOBILE STICKY CTA
       ================================================================ */

    mobileCtaAction: function () {
      if (this.currentStep === this.totalSteps) {
        this.addToCart();
      } else if (this.canProceed) {
        this.nextStep();
      }
    },

  }));
});
